<template>
  <div class="hero-birds" aria-hidden="true">
    <div class="hero-birds__parallax">
      <svg ref="svg" class="hero-birds__svg" focusable="false">
        <defs>
          <clipPath :id="moonClipId">
            <circle ref="moonDisc" cx="-999" cy="-999" r="1"/>
          </clipPath>
        </defs>
        <g class="hero-birds__flock">
          <!-- A fixed pool of birds; the loop assigns them to flocks and
               rewrites their outlines every frame. -->
          <g v-for="n in slotCount" :key="n" ref="bird" class="hero-bird" display="none">
            <path class="hero-bird__far"/>
            <path class="hero-bird__body"/>
            <path class="hero-bird__near"/>
          </g>
        </g>
        <!-- The same birds as dark silhouettes, clipped to the moon's disc:
             a bird crossing the moon at night reads against it instead of
             vanishing pale-on-pale. -->
        <g ref="moonlit" class="hero-birds__moonlit" :clip-path="'url(#' + moonClipId + ')'" opacity="0">
          <g v-for="n in slotCount" :key="'m' + n" ref="shade" class="hero-bird-shade" display="none">
            <path/>
            <path/>
            <path/>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
import { createLoop, seededRandom, lerp, clamp, exposeDebug, removeDebug } from './heroMotion';
import { flapPose, strokeAngle, camera, birdPaths, wingReach, STROKE, GLIDE_POSE, LEVEL_THETA, DEG } from './HeroBirdsRig';

// Flocks of articulated birds crossing the sky (the rig is in
// HeroBirdsRig.js). Each bird flies its own flap-flap-glide rhythm (bursts of
// wingbeats, then a glide in a shallow gull "M"), bobs against its wingbeat,
// climbs a little while it flaps and sinks while it glides, and keeps a
// loose, drifting slot in its flock's V, echelon or line.
//
// Flight paths are planned around the hero text: the logo, title, subtitle,
// button and stat cards are measured (read-only) and turned into a smooth
// "ceiling" every crossing stays above, climbing gently over the text block
// and sinking again beside it. A phone's text can fill the hero's width from
// just under its top edge, which would squeeze the birds into a strip a few
// px tall: then they fly behind the logo, title and subtitle instead, faded
// (the text sits above the scene), and only the button, the stat cards and
// the tagline stay out of bounds. Where the layout leaves a clear way up
// beside the text, flocks also take off from the forest, rising out of the
// trees (the entrance does this whenever it can). At night, a bird crossing
// the moon turns into a dark silhouette against it. One rAF loop drives
// every bird; colours are pure CSS, keyed on the theme.

const SLOTS = 12;
const SEED = 7121;
const TAU = Math.PI * 2;
const STEP = 1000 / 60;
// Entrance choreography: the first flock shows up here.
const FIRST_FLOCK_AT = 1600;
// Wings level on the downstroke: where a flap settles into a glide.
const GLIDE_THETA = LEVEL_THETA;

// How birds are seen: wings near head-on (yaw in degrees) and slightly from
// below; the body is turned `bodyTurn` degrees further toward profile.
const VIEW = { yawMin: 78, yawMax: 86, pitchMin: 24, pitchMax: 32, bodyTurn: 44 };

const FLAP = 0;
const SETTLE = 1;
const GLIDE = 2;

// How far a bird reaches above and below its centre (wings over a whole beat
// plus the body bob and some bank), in wingspans, for the views used here.
const REACH = (function () {
  let up = 0;
  let down = 0;
  [70, 80, 88].forEach((yaw) => {
    [VIEW.pitchMin - 2, VIEW.pitchMax + 2].forEach((pitch) => {
      const r = wingReach(yaw, pitch);
      up = Math.max(up, r.up);
      down = Math.max(down, r.down);
    });
  });
  return { up: up / 2 + 0.07, down: down / 2 + 0.07 };
})();

// Vertical freedom of a bird around its slot, in wingspans: its slow drift,
// and its climb while flapping / sink while gliding.
const DRIFT_Y = 0.05;
const ALT_MAX = 0.16;
const SLACK = 2 * (DRIFT_Y + ALT_MAX);
// In a lane too tight for that freedom the birds calm down to this share of
// it before they get smaller.
const CALM_MIN = 0.3;
// Steepest climb or descent a crossing takes to get over the hero text.
const LANE_SLOPE = Math.tan(11 * DEG);
// Hero text padding (px): parallax shifts the birds up to 10px x / 5px y.
const PAD_X = 14;
const PAD_Y = 6;
// Flight paths are sampled every DS px of arc length.
const DS = 3;
// How much wider a formation is drawn out while it takes off.
const RISE_SPREAD = 1.8;
// On phones, a bird passing behind the logo, title or subtitle fades to this
// share of its opacity, easing in and out with this time constant (ms).
const VEIL = 0.5;
const VEIL_MS = 280;
// Beyond the end of its path a flock flies on straight, levelling off over
// about this many px (see pathAt).
const LEVEL_OFF = 120;
// When the last flock leaves the sky earlier than planned (the hero got
// narrower under it), the next one comes within this long (ms).
const EMPTY_SKY_MS = 4000;

// Flatter formations to fall back to when a lane is tight.
const FLATTER = {
  v: ['v', 'echelon', 'line'],
  echelon: ['echelon', 'line'],
  line: ['line'],
  pair: ['pair', 'line'],
  solo: ['solo']
};

function smooth(t) {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

function r2(v) {
  return Math.round(v * 100) / 100;
}

// Smooth minimum / maximum: never above min(a, b) / below max(a, b), and
// without the kink a plain min/max would put in a flight path.
function smin(a, b, k) {
  return (a + b - Math.sqrt((a - b) * (a - b) + k * k)) / 2;
}

function smax(a, b, k) {
  return (a + b + Math.sqrt((a - b) * (a - b) + k * k)) / 2;
}

// Everything that depends on the hero's size.
function metrics(width, height) {
  const W = Math.max(width, 280);
  const H = Math.max(height, 320);
  const phone = W < 576;
  // Wingspan of the nearest birds: ~26px on phones, ~40px on a laptop. On a
  // phone even the farthest stay ~16px, or a flock reads as specks of dust.
  const spanNear = clamp(20 + W * 0.0165, 26, 48);
  return {
    W,
    H,
    phone,
    spanNear,
    spanFar: Math.max(phone ? 16 : 12, spanNear * 0.36),
    spanMin: phone ? 16 : 10,
    // Closest a wingtip comes to the hero's top edge.
    top: 3,
    // The band of sky birds prefer; the text block is avoided separately.
    bandTop: H * (phone ? 0.04 : 0.06),
    bandBottom: H * (phone ? 0.28 : 0.42),
    maxBirds: W < 576 ? 4 : (W < 1000 ? 7 : (W < 1600 ? 10 : 12))
  };
}

// Formation slots in wingspans: `s` along the path (negative = behind the
// leader) and `n` across it (negative = higher). The two arms of a V are
// staggered and kept a full wingspan apart, so wings never touch.
function formation(kind, count, rnd, tidy) {
  const s = [];
  const n = [];
  const sign = rnd() < 0.5 ? -1 : 1;
  const jitter = tidy ? 0.5 : 1;
  let minN = 0;
  let maxN = 0;
  for (let k = 0; k < count; k++) {
    let os = 0;
    let on = 0;
    if (k > 0) {
      if (kind === 'v') {
        const lvl = Math.ceil(k / 2);
        const upper = k % 2 === 1;
        os = -lvl * 1.2 - (upper ? 0 : 0.35);
        on = (upper ? -1 : 1) * lvl * 0.5;
      } else if (kind === 'echelon') {
        os = -k * 1.25;
        on = sign * k * 0.3;
      } else if (kind === 'pair') {
        os = -1.3;
        on = sign * 0.4;
      } else {
        os = -k * 1.35;
        on = (k % 2 ? 1 : -1) * 0.06;
      }
      os += (rnd() - 0.5) * 0.16 * jitter;
      on += (rnd() - 0.5) * 0.1 * jitter;
    }
    s.push(os);
    n.push(on);
    minN = Math.min(minN, on);
    maxN = Math.max(maxN, on);
  }
  return { s, n, minN, maxN };
}

// How much a formation is pressed together along its path while it takes
// off (it is drawn out RISE_SPREAD times across it): as much as possible
// while every two birds stay 1.25 wingspans apart, whichever way the climb
// turns them.
function climbAlong(form) {
  let a = 0.6;
  for (; a < 1.3; a += 0.05) {
    let ok = true;
    for (let i = 0; i < form.s.length && ok; i++) {
      for (let j = i + 1; j < form.s.length && ok; j++) {
        const ds = (form.s[i] - form.s[j]) * a;
        const dn = (form.n[i] - form.n[j]) * RISE_SPREAD;
        if (ds * ds + dn * dn < 1.25 * 1.25) ok = false;
      }
    }
    if (ok) break;
  }
  return a;
}

// Appends a cubic Bezier (without its first point) to a polyline.
function bezier(xs, ys, x0, y0, x1, y1, x2, y2, x3, y3, steps) {
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    xs.push(a * x0 + b * x1 + c * x2 + d * x3);
    ys.push(a * y0 + b * y1 + c * y2 + d * y3);
  }
}

function hitsAny(rects, x, y, hw, hh) {
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (x + hw > r.l && x - hw < r.r && y + hh > r.t && y - hh < r.b) return true;
  }
  return false;
}

function polyLength(xs, ys, upTo) {
  let len = 0;
  for (let i = 1; i < upTo; i++) {
    len += Math.sqrt((xs[i] - xs[i - 1]) * (xs[i] - xs[i - 1]) + (ys[i] - ys[i - 1]) * (ys[i] - ys[i - 1]));
  }
  return len;
}

// A polyline resampled every DS px of arc length, so a flock moves along it
// at a steady speed whatever its shape.
function resample(xs, ys) {
  const count = xs.length;
  const cum = new Float64Array(count);
  for (let i = 1; i < count; i++) {
    const dx = xs[i] - xs[i - 1];
    const dy = ys[i] - ys[i - 1];
    cum[i] = cum[i - 1] + Math.sqrt(dx * dx + dy * dy);
  }
  const n = Math.max(2, Math.floor(cum[count - 1] / DS) + 1);
  const px = new Float32Array(n);
  const py = new Float32Array(n);
  let j = 0;
  for (let k = 0; k < n; k++) {
    const s = k * DS;
    while (j < count - 2 && cum[j + 1] < s) j++;
    const seg = cum[j + 1] - cum[j] || 1;
    const fr = clamp((s - cum[j]) / seg, 0, 1);
    px[k] = xs[j] + (xs[j + 1] - xs[j]) * fr;
    py[k] = ys[j] + (ys[j + 1] - ys[j]) * fr;
  }
  const a = Math.min(4, n - 1);
  const t0x = px[a] - px[0];
  const t0y = py[a] - py[0];
  const t0 = Math.sqrt(t0x * t0x + t0y * t0y) || 1;
  const t1x = px[n - 1] - px[n - 1 - a];
  const t1y = py[n - 1] - py[n - 1 - a];
  const t1 = Math.sqrt(t1x * t1x + t1y * t1y) || 1;
  return { px, py, L: (n - 1) * DS, t0x: t0x / t0, t0y: t0y / t0, t1x: t1x / t1, t1y: t1y / t1 };
}

// Vertical offset of an element's current (entrance) transform, so the text
// is measured where it settles, not where its fade-up animation has it now.
function liftOf(el, stop) {
  let y = 0;
  for (let n = el; n && n !== stop; n = n.parentElement) {
    const t = window.getComputedStyle(n).transform;
    if (t && t !== 'none') {
      const v = t.slice(t.indexOf('(') + 1, -1).split(',');
      y += parseFloat(v.length === 16 ? v[13] : v[5]) || 0;
    }
  }
  return y;
}

export default {
  name: 'HeroBirds',
  inject: ['heroState'],
  data() {
    return {
      slotCount: SLOTS,
      moonClipId: 'hero-birds-moon-' + this._uid
    };
  },
  watch: {
    'heroState.running'() {
      this.sync();
    },
    'heroState.reducedMotion'() {
      this.restart();
    },
    'heroState.width'() {
      this.onResize();
    },
    'heroState.height'() {
      this.onResize();
    }
  },
  created() {
    // Simulation state lives outside Vue's reactivity on purpose.
    this.loop = createLoop(this.tick);
    this.debugPaused = false;
    this.pose = { alpha: 0, gamma: 0, beta: 0, delta: 0 };
    this.wingCam = { cy: 1, sy: 0, cp: 1, sp: 0 };
    this.bodyCam = { cy: 1, sy: 0, cp: 1, sp: 0 };
    this.pos = { x: 0, y: 0, s: 0, tx: 1, ty: 0 };
    this.pa = { x: 0, y: 0 };
    this.pb = { x: 0, y: 0 };
    this.pc = { x: 0, y: 0 };
    this.qa = { x: 0, y: 0 };
    this.qb = { x: 0, y: 0 };
    this.qc = { x: 0, y: 0 };
    this.layout = null;
    this.heroEl = null;
    this.moonEl = null;
    this.moon = null;
    this.moonCur = { x: 0, y: 0, r: 0 };
    this.moonDiscAt = { x: -999, y: -999 };
    this.moonNear = false;
    this.moonReadAt = -1e9;
    this.moonWatchUntil = 0;
    this.moonTimer = 0;
    this.layoutTimer = 0;
    this.settleTimer = 0;
    this.shown = [];
    this.shaded = [];
  },
  mounted() {
    this.nodes = (this.$refs.bird || []).map((g) => ({
      g,
      far: g.querySelector('.hero-bird__far'),
      body: g.querySelector('.hero-bird__body'),
      near: g.querySelector('.hero-bird__near'),
      op: -1,
      shown: false
    }));
    this.shades = (this.$refs.shade || []).map((g) => {
      const parts = g.querySelectorAll('path');
      return { g, far: parts[0], body: parts[1], near: parts[2], op: -1, shown: false };
    });
    const scene = this.$el.parentNode;
    this.heroEl = scene ? scene.parentNode : null;
    this.readMoonLater = () => {
      this.moonTimer = 0;
      this.moonReadAt = performance.now();
      this.readMoon();
    };

    this.measure();
    this.readLayout();
    this.restart();

    // A theme switch sets one orb and raises the other: follow the moon
    // closely while it moves.
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => this.watchMoon(3600));
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }
    // The text block can move without the hero resizing (web fonts, the
    // stats arriving): re-plan the lanes when it does.
    const content = this.heroEl ? this.heroEl.querySelector('.hero-content') : null;
    if (content && typeof ResizeObserver !== 'undefined') {
      this.contentObserver = new ResizeObserver(() => this.queueLayout(60));
      this.contentObserver.observe(content);
    }
    this.settleTimer = setTimeout(() => this.queueLayout(0), 1200);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!this.destroyed) this.queueLayout(0);
      });
    }
    // The sky's orb rises during the entrance.
    this.watchMoon(2600);

    exposeDebug('birds', {
      pause: () => {
        this.debugPaused = true;
        this.sync();
      },
      resume: () => {
        this.debugPaused = false;
        this.sync();
      },
      renderAt: (ms) => {
        this.debugPaused = true;
        this.sync();
        this.renderAt(ms);
      },
      spawnNow: () => {
        if (this.heroState.reducedMotion) return;
        this.spawnFlock(this.sim);
        this.draw();
      },
      // Steps the simulation without drawing (for checks over long runs).
      advance: (ms) => {
        const n = Math.round(ms / STEP);
        for (let i = 0; i < n; i++) this.step(STEP);
        return Math.round(this.sim.t);
      },
      snapshot: () => this.snapshot(),
      relayout: () => {
        this.readLayout();
        return this.layout ? Math.round(this.layout.cMin) : null;
      },
      state: () => ({
        t: Math.round(this.sim.t),
        nextSpawn: Math.round(this.sim.nextSpawn),
        flocks: this.sim.flocks.map((f) => ({
          kind: f.kind,
          type: f.type,
          dir: f.dir,
          depth: r2(f.depth),
          birds: f.birds.length,
          span: r2(f.span),
          dist: Math.round(f.dist),
          L: Math.round(f.L)
        }))
      }),
      layout: () => (this.layout ? {
        rects: this.layout.rects.map((r) => [Math.round(r.l), Math.round(r.t), Math.round(r.r), Math.round(r.b)]),
        soft: this.layout.soft.map((r) => [Math.round(r.l), Math.round(r.t), Math.round(r.r), Math.round(r.b)]),
        bandBottom: Math.round(this.bandBottom()),
        landTop: Math.round(this.layout.landTop),
        cMin: Math.round(this.layout.cMin),
        ceilAt: (x) => Math.round(this.ceilAt(x))
      } : null),
      rig: { flapPose, camera, birdPaths, wingReach, STROKE, GLIDE_POSE, VIEW, REACH }
    });
  },
  beforeDestroy() {
    this.destroyed = true;
    this.loop.stop();
    clearTimeout(this.moonTimer);
    clearTimeout(this.layoutTimer);
    clearTimeout(this.settleTimer);
    if (this.themeObserver) this.themeObserver.disconnect();
    if (this.contentObserver) this.contentObserver.disconnect();
    removeDebug('birds');
  },
  methods: {
    measure() {
      const w = this.heroState.width || this.$el.clientWidth;
      const h = this.heroState.height || this.$el.clientHeight;
      this.m = metrics(w, h);
    },
    onResize() {
      // Only new flocks pick up the new sizes and lanes; birds already in
      // the air keep their path. step() measures each crossing against the
      // current size, though: past a widened hero's old edge they fly on to
      // the new one, and once a narrowed hero's edge is behind them they
      // leave at once, freeing their slots.
      this.measure();
      this.queueLayout(80);
      this.watchMoon(800);
      if (this.heroState.reducedMotion) this.renderStill();
    },
    queueLayout(ms) {
      clearTimeout(this.layoutTimer);
      this.layoutTimer = setTimeout(() => {
        this.layoutTimer = 0;
        if (this.destroyed) return;
        this.readLayout();
        if (this.heroState.reducedMotion) this.renderStill();
      }, ms);
    },
    watchMoon(ms) {
      this.moonWatchUntil = Math.max(this.moonWatchUntil, performance.now() + ms);
    },
    restart() {
      this.loop.stop();
      this.sim = this.createSim();
      if (this.heroState.reducedMotion) {
        this.renderStill();
        return;
      }
      this.draw();
      this.sync();
    },
    sync() {
      const run = this.heroState.running && !this.heroState.reducedMotion && !this.debugPaused;
      if (run) {
        if (!this.loop.isRunning()) this.watchMoon(300);
        this.loop.start();
      } else {
        this.loop.stop();
      }
    },
    createSim() {
      const used = [];
      for (let i = 0; i < SLOTS; i++) used.push(null);
      return { t: 0, nextSpawn: FIRST_FLOCK_AT, flockCount: 0, flocks: [], used };
    },
    tick(dt, now) {
      this.step(dt);
      this.draw();
      // The moon is measured after this frame has painted (never in the
      // middle of the frame's writes): continuously while it moves, and only
      // now and then otherwise, when a bird is near it.
      if (!this.moonTimer && (now < this.moonWatchUntil || (this.moonNear && now - this.moonReadAt > 2500))) {
        this.moonTimer = setTimeout(this.readMoonLater, 0);
      }
    },
    // Re-simulates from the start with a fixed step, so any moment can be
    // inspected exactly (rAF is throttled in background tabs).
    renderAt(ms) {
      if (this.heroState.reducedMotion) {
        this.renderStill();
        return;
      }
      this.sim = this.createSim();
      let t = 0;
      while (t < ms) {
        const d = Math.min(STEP, ms - t);
        this.step(d);
        t += d;
      }
      this.readMoon();
      this.draw();
    },

    /* ------------------------------------------------------------------ */
    /* Layout: the hero text to fly around, and the moon                   */
    /* ------------------------------------------------------------------ */

    // Measures (read-only) the hero text, the stat cards and the landscape,
    // relative to the scene, and builds the lane ceiling from them. `rects`
    // are kept clear; `soft` ones (tight phone layouts only) may be flown
    // behind, faded.
    readLayout() {
      const scene = this.$el.parentNode;
      const hero = scene ? scene.parentNode : null;
      if (!scene || !hero) return;
      this.measure();
      const m = this.m;
      const o = scene.getBoundingClientRect();
      // The logo, title and subtitle (`text`), then the button, the stat
      // cards and the tagline (`rects`).
      const text = [];
      const rects = [];
      const add = (r, lift, list) => {
        if (!r || r.width < 1 || r.height < 1) return;
        list.push({
          l: r.left - o.left - PAD_X,
          r: r.right - o.left + PAD_X,
          t: r.top - o.top - lift - PAD_Y,
          b: r.bottom - o.top - lift + PAD_Y
        });
      };
      const content = hero.querySelector('.hero-content');
      if (content) {
        const logos = content.querySelectorAll('.hero-logo');
        for (let i = 0; i < logos.length; i++) {
          if (logos[i].offsetWidth) add(logos[i].getBoundingClientRect(), liftOf(logos[i], content), text);
        }
        // Text by its glyphs, not its (full-width) block.
        ['.hero-title', '.hero-subtitle'].forEach((sel) => {
          const el = content.querySelector(sel);
          if (!el) return;
          const range = document.createRange();
          range.selectNodeContents(el);
          add(range.getBoundingClientRect(), liftOf(el, content), text);
        });
        ['.hero-cta', '.stats-grid', '.hero-tagline'].forEach((sel) => {
          const el = content.querySelector(sel);
          if (el) add(el.getBoundingClientRect(), liftOf(el, content), rects);
        });
      }
      const land = scene.querySelector('.hero-landscape');
      let landTop;
      let landH;
      if (land && land.offsetHeight) {
        landTop = land.offsetTop;
        landH = land.offsetHeight;
      } else {
        landH = clamp(window.innerWidth * 0.17, 150, 270);
        landTop = m.H - landH;
      }
      this.layout = { rects: text.concat(rects), soft: [], softFloor: 0, landTop, landH, ceil: null, cx0: 0, cdx: 4, cMin: 1e5 };
      this.buildCeiling();
      // On a phone the logo, title and subtitle can span the hero from just
      // under its top edge: kept clear, they would leave the birds a strip a
      // few px tall, so then the birds fly behind them (faded) and only the
      // button, the stat cards and the tagline stay out of bounds.
      if (m.phone && text.length && this.layout.cMin - m.top < m.spanNear * 2) {
        this.layout.rects = rects;
        this.layout.soft = text;
        this.layout.softFloor = Math.max.apply(null, text.map((r) => r.t));
        this.buildCeiling();
      }
    },

    // The lowest a bird may fly at each x so that it stays above the text,
    // as a slope-limited, rounded envelope: a crossing climbs gently over the
    // text block and sinks again beside it, never a sudden hop.
    buildCeiling() {
      const L = this.layout;
      const m = this.m;
      const x0 = -260;
      const dx = 4;
      const n = Math.ceil((m.W + 520) / dx) + 1;
      const c = new Float32Array(n);
      c.fill(1e5);
      L.rects.forEach((r) => {
        const i0 = Math.max(0, Math.ceil((r.l - x0) / dx));
        const i1 = Math.min(n - 1, Math.floor((r.r - x0) / dx));
        for (let i = i0; i <= i1; i++) {
          if (r.t < c[i]) c[i] = r.t;
        }
      });
      const k = dx * LANE_SLOPE;
      for (let i = 1; i < n; i++) c[i] = Math.min(c[i], c[i - 1] + k);
      for (let i = n - 2; i >= 0; i--) c[i] = Math.min(c[i], c[i + 1] + k);
      // Round the corners: an erosion followed by a box blur of the same
      // radius never rises above the envelope it smooths.
      const R = 6;
      const e = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        let v = c[i];
        for (let d = -R; d <= R; d++) {
          const j = i + d;
          if (j >= 0 && j < n && c[j] < v) v = c[j];
        }
        e[i] = v;
      }
      let cMin = 1e5;
      for (let i = 0; i < n; i++) {
        let sum = 0;
        let cnt = 0;
        for (let d = -R; d <= R; d++) {
          const j = i + d;
          if (j >= 0 && j < n) {
            sum += e[j];
            cnt += 1;
          }
        }
        c[i] = sum / cnt;
        const x = x0 + i * dx;
        if (x >= 0 && x <= m.W && c[i] < cMin) cMin = c[i];
      }
      L.ceil = c;
      L.cx0 = x0;
      L.cdx = dx;
      L.cMin = cMin;
    },

    ceilAt(x) {
      const L = this.layout;
      if (!L || !L.ceil) return 1e5;
      const c = L.ceil;
      const u = (x - L.cx0) / L.cdx;
      if (u <= 0) return c[0];
      if (u >= c.length - 1) return c[c.length - 1];
      const i = Math.floor(u);
      return c[i] + (c[i + 1] - c[i]) * (u - i);
    },

    // True when a box of half-size hw x hh at (x, y) overlaps the text kept
    // clear (and, with `withSoft`, the text birds may fly behind too).
    blocked(x, y, hw, hh, withSoft) {
      const L = this.layout;
      if (!L) return false;
      return hitsAny(L.rects, x, y, hw, hh) || (!!withSoft && hitsAny(L.soft, x, y, hw, hh));
    },

    // 1 when a bird at (x, y) is behind the text it may fly behind (phones
    // only), 0 otherwise.
    veilAt(x, y, span) {
      const L = this.layout;
      if (!L || !L.soft.length) return 0;
      return hitsAny(L.soft, x, y, span * 0.3, span * 0.15) ? 1 : 0;
    },

    // True when a flock taking off (spread `lat` wingspans to each side of
    // its path, shrinking to `end` of its size as it flies away) can fly the
    // visible part of a polyline without passing behind the text or the
    // stat cards.
    clearRise(xs, ys, span, end, lat) {
      const L = this.layout;
      const yMax = L.landTop + L.landH * 0.1;
      const total = polyLength(xs, ys, xs.length) || 1;
      let s = 0;
      for (let i = 0; i < xs.length; i++) {
        if (i > 0) s += Math.sqrt((xs[i] - xs[i - 1]) * (xs[i] - xs[i - 1]) + (ys[i] - ys[i - 1]) * (ys[i] - ys[i - 1]));
        const r = span * (1 - (1 - end) * smooth(s / total));
        if (ys[i] < yMax && this.blocked(xs[i], ys[i], r * (lat + 0.55), r * (0.5 + 0.5 * lat))) return false;
      }
      return true;
    },

    // How big a bird is at arc length s of its flock's path: flocks that
    // took off from the forest shrink as they fly away into the distance.
    scaleAt(f, s) {
      if (f.scLen <= 0 || s <= 0) return 1;
      if (s >= f.scLen) return f.sc1;
      return 1 - (1 - f.sc1) * smooth(s / f.scLen);
    },

    // Where the sky's moon disc is right now (it rises and sets with the
    // theme), in this SVG's coordinates, and how visible it is. Read-only:
    // HeroSky owns the moon; without one, the silhouettes simply stay off.
    readMoon() {
      let el = this.moonEl;
      if (!el || !el.isConnected) {
        const scene = this.$el.parentNode;
        el = scene ? scene.querySelector('.hero-sky .moon:not(.moon-glare)') : null;
        this.moonEl = el;
      }
      const svg = this.$refs.svg;
      if (!el || !svg) {
        this.setMoon(null);
        return;
      }
      const r = el.getBoundingClientRect();
      let opacity = parseFloat(window.getComputedStyle(el).opacity);
      if (isNaN(opacity)) opacity = 1;
      // The orb around it fades in during the entrance.
      if (el.parentElement) {
        const outer = parseFloat(window.getComputedStyle(el.parentElement).opacity);
        if (!isNaN(outer)) opacity *= outer;
      }
      if (!r.width || opacity < 0.02) {
        this.setMoon(null);
        return;
      }
      const s = svg.getBoundingClientRect();
      const par = this.parallax();
      // The disc is r=50 in a 120 box drawn at 92% of the moon's box.
      this.setMoon({
        x: r.left + r.width / 2 - s.left,
        y: r.top + r.height / 2 - s.top,
        r: r.width * 0.383,
        opacity,
        px: par.x,
        py: par.y
      });
    },

    setMoon(moon) {
      this.moon = moon;
      const disc = this.$refs.moonDisc;
      const layer = this.$refs.moonlit;
      if (!disc || !layer) return;
      if (moon) disc.setAttribute('r', r2(moon.r));
      layer.setAttribute('opacity', moon ? r2(moon.opacity) : 0);
    },

    parallax() {
      const style = this.heroEl ? this.heroEl.style : null;
      this.par = this.par || { x: 0, y: 0 };
      this.par.x = style ? parseFloat(style.getPropertyValue('--hero-px')) || 0 : 0;
      this.par.y = style ? parseFloat(style.getPropertyValue('--hero-py')) || 0 : 0;
      return this.par;
    },

    // The moon as last measured, moved by the pointer parallax since then
    // (moon layer 4px / 3px, birds 10px / 5px), without measuring again.
    moonNow() {
      const moon = this.moon;
      if (!moon) return null;
      const par = this.parallax();
      const cur = this.moonCur;
      cur.x = moon.x - 6 * (par.x - moon.px);
      cur.y = moon.y - 2 * (par.y - moon.py);
      cur.r = moon.r;
      const at = this.moonDiscAt;
      if (Math.abs(cur.x - at.x) > 0.05 || Math.abs(cur.y - at.y) > 0.05) {
        at.x = cur.x;
        at.y = cur.y;
        const disc = this.$refs.moonDisc;
        if (disc) {
          disc.setAttribute('cx', r2(cur.x));
          disc.setAttribute('cy', r2(cur.y));
        }
      }
      return cur;
    },

    /* ------------------------------------------------------------------ */
    /* Simulation                                                          */
    /* ------------------------------------------------------------------ */

    step(dt) {
      const sim = this.sim;
      const m = this.m;
      sim.t += dt;
      if (sim.t >= sim.nextSpawn) this.spawnFlock(sim);
      const veilK = 1 - Math.exp(-dt / VEIL_MS);
      let left = false;

      for (let i = sim.flocks.length - 1; i >= 0; i--) {
        const f = sim.flocks[i];
        f.age += dt;
        // Anything flying away into the distance seems slower as it shrinks.
        f.sc = this.scaleAt(f, f.dist);
        f.dist += f.speed * f.sc * dt;
        f.formW = f.formLen > 0 ? smooth((f.dist - f.formFrom) / f.formLen) : 1;
        // A flock was planned for the hero's size when it set off, which may
        // have changed since: it stays while any bird is still in view (so
        // past a widened hero's old edge the birds fly on to the new one,
        // never vanishing mid-sky) or still has path to fly toward the view,
        // and leaves as soon as every bird is beyond the edge it flies out
        // of (a narrowed hero frees its slots at once).
        let keep = false;
        for (let j = 0; j < f.birds.length; j++) {
          const b = f.birds[j];
          this.updateBird(b, f, dt);
          const p = this.place(b, f, false);
          b.rx = p.x;
          b.ry = p.y;
          b.rs = p.span;
          b.s = p.s;
          // A little beyond where draw() culls, as separation can still
          // move a bird there.
          const edge = p.span * 1.8;
          const past = f.dir > 0 ? p.x > m.W + edge : p.x < -edge;
          const inView = p.x > -edge && p.x < m.W + edge && p.y > -edge && p.y < m.H + edge;
          if (inView || (p.s < f.L && !past)) keep = true;
          const veil = this.veilAt(p.x, p.y, p.span);
          b.veil = b.veil < 0 ? veil : b.veil + (veil - b.veil) * veilK;
        }
        this.separate(f, dt);
        if (!keep || f.age > 150000) {
          f.birds.forEach((b) => {
            sim.used[b.slot] = null;
          });
          sim.flocks.splice(i, 1);
          left = true;
        }
      }
      // The next flock was timed for the crossings planned before a resize:
      // an empty sky does not wait for it for long.
      if (left && !sim.flocks.length && sim.nextSpawn > sim.t + EMPTY_SKY_MS) {
        sim.nextSpawn = sim.t + EMPTY_SKY_MS;
      }
    },

    // Flap-flap-glide: a burst of wingbeats, a smooth settle into the glide
    // pose (the stroke decelerates to wings-level), a glide, and an eased
    // restart of the stroke. `e` blends the glide pose (0) and the flap (1).
    // Birds taking off beat faster and do not glide until they level off.
    updateBird(b, f, dt) {
      const sec = dt / 1000;
      const climbing = f.climbEnd > 0 && b.s < f.climbEnd;
      b.boost += ((climbing ? 1.3 : 1) - b.boost) * (1 - Math.exp(-sec / 0.6));
      const freq = b.freq * b.boost;
      b.modeT += dt;
      if (b.mode === FLAP) {
        const ramp = smooth(b.modeT / 240);
        b.e = b.e0 + (1 - b.e0) * ramp;
        b.theta += TAU * freq * sec * (0.3 + 0.7 * ramp);
        if (b.theta >= TAU) {
          b.theta -= TAU;
          b.beats -= 1;
          if (b.beats <= 0 && climbing) b.beats = 2;
          if (b.beats <= 0) {
            b.mode = SETTLE;
            b.modeT = 0;
            b.settleFrom = b.theta;
            // Starts at the flap's own angular speed and eases to a stop.
            b.settleDur = (2 * (GLIDE_THETA - b.theta) / (TAU * freq)) * 1000;
          }
        }
      } else if (b.mode === SETTLE) {
        const u = clamp(b.modeT / b.settleDur, 0, 1);
        b.theta = b.settleFrom + (GLIDE_THETA - b.settleFrom) * (1 - (1 - u) * (1 - u));
        if (u >= 1) {
          b.mode = GLIDE;
          b.modeT = 0;
          b.e0 = b.e;
          b.glideDur = lerp(600, 2000, b.rnd());
        }
      } else {
        b.e = b.e0 * (1 - smooth(b.modeT / 450));
        if (b.modeT >= b.glideDur || climbing) {
          b.mode = FLAP;
          b.modeT = 0;
          b.e0 = b.e;
          b.beats = 3 + Math.floor(b.rnd() * 5);
        }
      }

      // Rises a little while flapping and sinks a little while gliding,
      // always within its slot's vertical freedom...
      const prev = b.alt;
      b.alt += ((0.5 - b.e) * 2 * b.altMax - b.alt) * (1 - Math.exp(-sec / 1.4));
      b.vAlt = sec > 0 ? (b.alt - prev) / sec : 0;
      // ...and drops back a touch in the glide, catching up when flapping.
      b.sOff += ((b.e - 0.5) * 0.24 * b.span - b.sOff) * (1 - Math.exp(-sec / 1.5));
    },

    // Keeps the birds of a flock from touching: a pair whose wings would
    // overlap eases apart, vertically in their slot order and along the
    // path, instead of crossing through each other.
    separate(f, dt) {
      const birds = f.birds;
      const count = birds.length;
      if (count < 2) return;
      for (let i = 0; i < count; i++) {
        birds[i].sepT = 0;
        birds[i].sepST = 0;
      }
      for (let i = 0; i < count; i++) {
        for (let j = i + 1; j < count; j++) {
          const a = birds[i];
          const b = birds[j];
          const span = (a.rs + b.rs) / 2;
          const reach = span * 1.1;
          const dx = Math.abs(a.rx - b.rx);
          if (dx >= reach) continue;
          const need = span * 0.74;
          const dy = b.ry - a.ry;
          const ady = Math.abs(dy);
          if (ady >= need) continue;
          const dir = ady > 0.5 ? (dy > 0 ? 1 : -1) : (b.offN >= a.offN ? 1 : -1);
          const push = (need - ady) * Math.min(1, (reach - dx) / (span * 0.3)) * 0.5;
          a.sepT -= dir * push;
          b.sepT += dir * push;
          const ahead = b.s >= a.s ? 1 : -1;
          const pushS = (reach - dx) * Math.min(1, (need - ady) / (span * 0.3)) * 0.25;
          a.sepST -= ahead * pushS;
          b.sepST += ahead * pushS;
        }
      }
      const k = 1 - Math.exp(-dt / 350);
      for (let i = 0; i < count; i++) {
        const b = birds[i];
        const lim = b.rs * 0.3;
        b.sep += (clamp(b.sepT, -lim, lim) - b.sep) * k;
        b.sepS += (clamp(b.sepST, -lim, lim) - b.sepS) * k;
      }
    },

    // A point of a flock's path at arc length s: straight on before its
    // start, and beyond its end straight on too, levelling off smoothly (C1)
    // into level flight its way, so birds carried past it by a widened hero
    // neither climb nor sink into the text on their way to the new edge.
    pathAt(f, s, out) {
      const px = f.px;
      const py = f.py;
      const n = px.length;
      if (s <= 0) {
        out.x = px[0] + f.t0x * s;
        out.y = py[0] + f.t0y * s;
      } else if (s >= f.L) {
        const d = s - f.L;
        const k = LEVEL_OFF * (1 - Math.exp(-d / LEVEL_OFF));
        out.x = px[n - 1] + f.t1x * k + f.dir * (d - k);
        out.y = py[n - 1] + f.t1y * k;
      } else {
        const u = s / DS;
        const i = Math.min(n - 2, Math.floor(u));
        const fr = u - i;
        out.x = px[i] + (px[i + 1] - px[i]) * fr;
        out.y = py[i] + (py[i + 1] - py[i]) * fr;
      }
      return out;
    },

    // Screen position of a bird: its slot in the flock along the flock's
    // path (a loose two-file column while taking off, opening into the
    // formation as the flock levels off), plus its own slow drift, climb or
    // sink, and separation from its neighbours. The whole formation shrinks
    // with a flock flying away; `sc` is the bird's own size factor.
    place(b, f, withSep) {
      const t = f.age / 1000;
      const w = f.formW;
      const k = f.sc;
      const offS = (b.strS + (b.offS - b.strS) * w) * k;
      const offN = (b.strN + (b.offN - b.strN) * w) * k;
      const s = f.dist + offS + (b.sOff + b.dAx * Math.sin(TAU * b.dFx * t + b.dPx)) * k + (withSep ? b.sepS : 0);
      const a = this.pathAt(f, s - 6, this.pa);
      const c = this.pathAt(f, s + 6, this.pc);
      let tx = c.x - a.x;
      let ty = c.y - a.y;
      const len = Math.sqrt(tx * tx + ty * ty) || 1;
      tx /= len;
      ty /= len;
      const p = this.pathAt(f, s, this.pb);
      // Across the path, positive = lower whichever way the flock flies.
      const nx = -ty * f.dir;
      const ny = tx * f.dir;
      const y = p.y + ny * offN + (b.dAy * Math.sin(TAU * b.dFy * t + b.dPy) + b.alt) * k + (withSep ? b.sep : 0);
      const sc = this.scaleAt(f, s);
      const span = b.span * sc;
      // Never closer to the top edge than a raised wing needs: a smooth
      // (C1) soft limit instead of a hard clamp, so nothing flattens out.
      const limit = this.m.top + span * REACH.up;
      const knee = span * 0.4;
      this.pos.x = p.x + nx * offN;
      this.pos.y = y >= limit + knee ? y : limit + knee * Math.exp((y - limit - knee) / knee);
      this.pos.s = s;
      this.pos.tx = tx;
      this.pos.ty = ty;
      this.pos.sc = sc;
      this.pos.span = span;
      return this.pos;
    },

    pickSize(rnd) {
      const r = rnd();
      if (this.m.phone) return r < 0.22 ? 1 : (r < 0.6 ? 2 : 3);
      if (r < 0.12) return 1;
      if (r < 0.22) return 2;
      return 3 + Math.floor(rnd() * 4);
    },

    pickDepth(rnd) {
      const r = rnd();
      if (r < 0.28) return lerp(0.1, 0.4, rnd());
      if (r < 0.66) return lerp(0.4, 0.7, rnd());
      return lerp(0.7, 1, rnd());
    },

    // Far flocks take the first free slots and near ones the last, so in the
    // DOM (paint order) nearer birds usually end up on top.
    takeSlots(sim, n, depth) {
      const free = [];
      for (let i = 0; i < SLOTS; i++) {
        if (!sim.used[i]) free.push(i);
      }
      if (depth >= 0.5) free.reverse();
      return free.slice(0, n);
    },

    // The formation (and size) a flock can keep through the narrowest part
    // of its lane, over the hero text. A tight lane first calms the birds'
    // vertical wandering, then flattens the formation (V -> echelon -> line),
    // and only then makes the birds smaller (that is, farther away). The
    // entrance (`tidy`) would rather fly with fewer birds than with tiny ones.
    fitFormation(seed, n, kind, depth, tidy) {
      const m = this.m;
      const room = this.layout && this.layout.ceil ? this.layout.cMin - m.top : 1e5;
      const want = lerp(m.spanFar, m.spanNear, depth);
      const kinds = FLATTER[kind];
      const tries = [];
      for (let c = n; c >= (tidy ? Math.min(n, 3) : n); c--) {
        for (let i = 0; i < kinds.length; i++) tries.push([kinds[i], c]);
      }
      let best = null;
      for (let i = 0; i < tries.length; i++) {
        const form = formation(tries[i][0], tries[i][1], seededRandom(seed + 31), tidy);
        const base = form.maxN - form.minN + REACH.up + REACH.down;
        const span = Math.min(want, room / (base + CALM_MIN * SLACK));
        const cand = { kind: tries[i][0], form, base, span };
        if (span >= want * 0.8) {
          best = cand;
          break;
        }
        if (!best || span > best.span * 1.05) best = cand;
      }
      best.span = Math.max(best.span, m.spanMin);
      best.calm = clamp((room / best.span - best.base) / SLACK, CALM_MIN, 1);
      // Smaller birds read as farther away: fainter and slower too.
      const fromSpan = (best.span - m.spanFar) / Math.max(1, m.spanNear - m.spanFar);
      best.depth = clamp(Math.min(depth, fromSpan), 0, 1);
      return best;
    },

    // Vertical room a fitted formation needs above and below its path: the
    // highest its leader may fly (`lo`, wingtips just clear of the top edge),
    // the highest it prefers (`top`, its highest bird at the band's top) and
    // the lowest (`hi`). The text ceiling may still lift it above `top`.
    extents(fit) {
      const m = this.m;
      const slackY = fit.calm * (DRIFT_Y + ALT_MAX);
      const above = fit.span * (-fit.form.minN + REACH.up + slackY);
      const below = fit.span * (fit.form.maxN + REACH.down + slackY);
      const lo = m.top + above;
      const hi = Math.max(lo, this.bandBottom() - below);
      const top = clamp(m.bandTop + above - fit.span * REACH.up, lo, hi);
      return { above, below, lo, top, hi };
    },

    // The lowest the birds prefer to fly. Where they may fly behind the
    // text (tight phone layouts), no lower than the top of its lowest line:
    // the logo and the title make a fine backdrop, the subtitle's small
    // print is better left clear.
    bandBottom() {
      const L = this.layout;
      return L && L.soft.length ? Math.min(this.m.bandBottom, L.softFloor) : this.m.bandBottom;
    },

    // A stretch of crossing from xa to xb: a gentle curve with a change of
    // altitude, kept under the text ceiling and inside the sky band.
    appendLane(xs, ys, xa, xb, y0, y1, wave, ext, span, eased) {
      const steps = Math.max(2, Math.ceil(Math.abs(xb - xa) / DS));
      const k = span * 0.5 + 10;
      for (let i = 0; i <= steps; i++) {
        const u = i / steps;
        const x = xa + (xb - xa) * u;
        let y = y0 + (y1 - y0) * smooth(u) + wave.amp * Math.sin(TAU * wave.cycles * u + wave.ph) * (eased ? smooth(u * 5) : 1);
        y = smin(y, this.ceilAt(x) - ext.below, k);
        y = smax(y, ext.lo, k * 0.5);
        xs.push(x);
        ys.push(y);
      }
    },

    // A flock crossing the sky from one side to the other.
    planCross(seed, rnd, n, kind, depth, first) {
      const m = this.m;
      const dir = first || rnd() < 0.7 ? 1 : -1;
      const fit = this.fitFormation(seed, n, kind, depth, first);
      const ext = this.extents(fit);
      const lo = ext.top;
      const hi = ext.hi;
      const margin = fit.span * 1.6;
      let y0;
      let y1;
      let wave;
      if (first) {
        // The entrance: a graceful climb in from the left that levels off
        // high in the sky (over the text, which the ceiling takes care of).
        y0 = lerp(lo, hi, 0.42);
        y1 = lerp(lo, hi, 0.08);
        wave = { amp: (hi - lo) * 0.02, cycles: 0.6, ph: Math.PI };
      } else {
        const r = Math.pow(rnd(), 1.2) * 0.85 + (1 - fit.depth) * 0.15;
        y0 = lerp(lo, hi, r);
        // As many flocks sink across the sky as climb.
        y1 = clamp(y0 + (hi - lo) * lerp(-0.28, 0.28, rnd()), lo, hi);
        wave = { amp: (hi - lo) * lerp(0.015, 0.05, rnd()), cycles: lerp(0.5, 1.2, rnd()), ph: rnd() * TAU };
      }
      const xa = dir > 0 ? -margin : m.W + margin;
      const xb = dir > 0 ? m.W + margin : -margin;
      const xs = [];
      const ys = [];
      this.appendLane(xs, ys, xa, xb, y0, y1, wave, ext, fit.span, false);
      return {
        type: 'cross',
        kind: fit.kind,
        count: fit.form.s.length,
        dir,
        fit,
        xs,
        ys,
        climbLen: 0,
        // The entrance starts right at the edge so it shows on cue.
        startAt: first ? margin - fit.span * 0.9 : 0
      };
    },

    // A flock taking off from the forest beside the text: it climbs steeply
    // out of the trees and flies away into the distance (shrinking as it
    // goes), either turning in over the text into a high crossing, small
    // enough by then to fit the lane, or swinging out of the near side of the
    // sky. Null when no clear way up exists in this layout.
    planRise(seed, rnd, n, kind, depth, first) {
      const m = this.m;
      const L = this.layout;
      if (!L || !L.ceil) return null;
      const want = lerp(m.spanFar, m.spanNear, depth);
      const yStart = L.landTop + L.landH * 0.55;
      // The crossing it settles into: some way off already, and no bigger
      // than the lane over the text allows.
      const fit = this.fitFormation(seed, n, kind, depth * 0.78, true);
      const end = fit.span / want;
      const reachY = rnd();
      const leaveY = rnd();
      const tilt = rnd();
      const sides = rnd() < 0.5 ? [-1, 1] : [1, -1];
      if (end >= 0.3) {
        // The entrance prefers the sun / moon side, so it flies past it.
        const order = first ? [1, -1] : sides;
        const fracs = [0.12, 0.09, 0.16, 0.06, 0.2, 0.26];
        for (let a = 0; a < order.length; a++) {
          for (let i = 0; i < fracs.length; i++) {
            const plan = this.riseCross(order[a], fracs[i], yStart, want, fit, reachY, tilt);
            if (plan) {
              plan.depth = depth;
              return plan;
            }
          }
        }
      }
      // Swinging away, the birds keep their take-off column, so formation
      // limits do not apply.
      const order = first ? [1, -1] : sides;
      const fracs = [0.2, 0.15, 0.11, 0.08, 0.05];
      const column = { kind: 'column', form: { s: [0], n: [0], minN: 0, maxN: 0 }, span: want, depth, calm: 1 };
      for (let a = 0; a < order.length; a++) {
        for (let i = 0; i < fracs.length; i++) {
          const plan = this.riseLeave(order[a], fracs[i], yStart, want, column, leaveY);
          if (plan) {
            plan.count = n;
            plan.depth = depth;
            return plan;
          }
        }
      }
      return null;
    },

    riseCross(side, frac, yStart, want, fit, reachY, tilt) {
      const m = this.m;
      const ext = this.extents(fit);
      const dir = -side;
      const xL = side < 0 ? m.W * frac : m.W * (1 - frac);
      const yAim = lerp(ext.top, ext.hi, 0.08 + 0.27 * reachY);
      // [share of the climb before it turns, width of the turn / height]:
      // a long straight climb out of the trees, then a wide, banked turn.
      const shapes = [[0.6, 0.6], [0.72, 0.5], [0.5, 0.8], [0.82, 0.42], [0.66, 1], [0.9, 0.34]];
      for (let i = 0; i < shapes.length; i++) {
        const wJ = clamp((yStart - yAim) * shapes[i][1], 120, m.W * 0.45);
        const xJ = xL + dir * wJ;
        const yJ = Math.max(ext.lo, Math.min(yAim, this.ceilAt(xJ) - ext.below - 4, this.ceilAt(xJ + dir * 60) - ext.below - 4));
        if (yStart - yJ < want * 3) continue;
        // The crossing first, then the climb is shaped to run into it
        // without a kink (same point, same direction).
        const xb = dir > 0 ? m.W + fit.span * 1.6 : -fit.span * 1.6;
        const y1 = clamp(yJ + (ext.hi - ext.top) * lerp(-0.05, 0.3, tilt), ext.top, ext.hi);
        const lx = [];
        const ly = [];
        this.appendLane(lx, ly, xJ, xb, yJ, y1, { amp: (ext.hi - ext.lo) * 0.03, cycles: 0.8, ph: 0 }, ext, fit.span, true);
        const yE = ly[0];
        const slope = (ly[1] - ly[0]) / (lx[1] - lx[0]);
        const h = yStart - yE;
        const xs = [xL];
        const ys = [yStart];
        bezier(xs, ys, xL, yStart, xL + dir * 0.05 * h, yStart - shapes[i][0] * h,
          xJ - dir * 0.6 * wJ, yE - slope * dir * 0.6 * wJ, xJ, yE, 90);
        const climbLen = polyLength(xs, ys, xs.length);
        if (!this.clearRise(xs, ys, want, fit.span / want, RISE_SPREAD * Math.max(-fit.form.minN, fit.form.maxN))) continue;
        for (let j = 1; j < lx.length; j++) {
          xs.push(lx[j]);
          ys.push(ly[j]);
        }
        return { type: 'rise', kind: fit.kind, count: fit.form.s.length, dir, fit, want, end: fit.span / want, xs, ys, climbLen, startAt: 0 };
      }
      return null;
    },

    riseLeave(side, frac, yStart, want, fit, leaveY) {
      const m = this.m;
      const dir = side;
      const end = 0.62;
      const xL = side < 0 ? m.W * frac : m.W * (1 - frac);
      const xOut = side < 0 ? -want * 3 : m.W + want * 3;
      const yEnd = Math.max(m.top + want * (REACH.up + 0.6), lerp(m.bandTop + want, m.bandBottom * 0.8, leaveY));
      const h = yStart - yEnd;
      if (h < want * 3) return null;
      const w = Math.abs(xOut - xL);
      const xs = [xL];
      const ys = [yStart];
      bezier(xs, ys, xL, yStart, xL + dir * 0.08 * h, yStart - 0.6 * h, xOut - dir * 0.55 * w, yEnd + 0.06 * h, xOut, yEnd, 80);
      if (!this.clearRise(xs, ys, want, end, 0.6)) return null;
      const climbLen = polyLength(xs, ys, xs.length);
      return { type: 'leave', kind: 'column', count: 1, dir, fit, want, end, xs, ys, climbLen, startAt: 0 };
    },

    spawnFlock(sim) {
      const m = this.m;
      const index = sim.flockCount;
      const seed = SEED + index * 7919;
      const rnd = seededRandom(seed);
      const first = index === 0;
      sim.flockCount += 1;

      let active = 0;
      for (let i = 0; i < SLOTS; i++) {
        if (sim.used[i]) active += 1;
      }
      let n = first ? (m.phone ? 3 : 5) : this.pickSize(rnd);
      n = Math.min(n, m.maxBirds - active);
      if (n < 1) {
        // The sky is full: try again shortly.
        sim.nextSpawn = sim.t + 2500;
        return;
      }
      const depth = first ? 0.86 : this.pickDepth(rnd);
      const kind = n === 1 ? 'solo' : (n === 2 ? 'pair' : (first || rnd() < 0.6 ? 'v' : 'echelon'));

      // Taking off from the forest: the entrance whenever the layout leaves
      // a clear way up beside the text, and now and then afterwards (more
      // often where the lane over the text is too tight for big birds).
      let plan = null;
      if (!m.phone && this.layout && this.layout.ceil) {
        const tight = this.layout.cMin - m.top < m.spanNear * 1.6;
        if (first || rnd() < (tight ? 0.35 : 0.18)) {
          plan = this.planRise(seed, rnd, n, kind, Math.max(depth, 0.72), first);
        }
      }
      if (!plan) plan = this.planCross(seed, rnd, n, kind, depth, first);
      const f = this.makeFlock(sim, plan, seed, rnd, first);
      sim.flocks.push(f);

      // The next flock comes about when this one has crossed, so the sky is
      // rarely empty and never crowded.
      const crossMs = (f.L - f.dist + f.tail) / (f.speed * (1 + f.sc1) / 2);
      sim.nextSpawn = sim.t + (first
        ? clamp(crossMs * 0.6, 5000, 9000)
        : clamp(crossMs * lerp(0.8, 1.3, rnd()), m.phone ? 5500 : 6500, 16000));
    },

    makeFlock(sim, plan, seed, rnd, first) {
      const m = this.m;
      const fit = plan.fit;
      const path = resample(plan.xs, plan.ys);
      const rise = plan.type !== 'cross';
      // A rising flock is sized for its take-off, close by; it shrinks to
      // its crossing size as it flies away.
      const span = rise ? plan.want : fit.span;
      const depth = rise ? plan.depth : fit.depth;
      const f = {
        id: sim.flockCount,
        type: plan.type,
        kind: plan.kind,
        dir: plan.dir,
        depth,
        span,
        // px per ms; smaller (farther) birds look slower.
        speed: (span * lerp(4.6, 4.0, depth) * (m.phone ? 0.75 : 1)) / 1000,
        px: path.px,
        py: path.py,
        L: path.L,
        t0x: path.t0x,
        t0y: path.t0y,
        t1x: path.t1x,
        t1y: path.t1y,
        dist: plan.startAt,
        age: 0,
        climbEnd: rise ? Math.min(plan.climbLen, path.L) : 0,
        // A rising flock eases from its drawn-out take-off shape into its
        // formation as it levels off.
        formFrom: plan.type === 'rise' ? plan.climbLen * 0.7 : 0,
        formLen: plan.type === 'rise' ? plan.climbLen * 0.3 + span * 5 : 0,
        formW: plan.type === 'rise' ? 0 : 1,
        sc: 1,
        sc1: rise ? plan.end : 1,
        scLen: rise ? plan.climbLen : 0,
        // ...and fades a little with the distance, down to this share.
        fade: rise ? lerp(0.5, 1, depth * plan.end) / lerp(0.5, 1, depth) : 1,
        tail: 0,
        birds: []
      };
      if (rise) {
        // Start with the leader just below the treetops, so it rises out of
        // the forest on cue and the others follow one by one.
        const yEmerge = this.layout.landTop + this.layout.landH * 0.3;
        let k = 0;
        while (k < f.px.length - 1 && f.py[k] > yEmerge) k++;
        f.dist = Math.max(0, k * DS - span * 0.2);
      }

      const yaw = lerp(VIEW.yawMin, VIEW.yawMax, rnd());
      const pitch = lerp(VIEW.pitchMin, VIEW.pitchMax, rnd());
      const form = fit.form;
      const along = rise ? climbAlong(form) : 1;
      const slots = this.takeSlots(sim, plan.count, depth);
      for (let k = 0; k < slots.length; k++) {
        const fs = plan.type === 'leave' ? 0 : form.s[k];
        const fn = plan.type === 'leave' ? 0 : form.n[k];
        // Taking off, the formation is drawn out across the climb and
        // pressed together along it (the birds lift off one after another,
        // never wing to wing); it eases into its true shape as it levels
        // off. Birds that swing away keep a loose two-file column.
        let strS = fs * span;
        let strN = fn * span;
        if (plan.type === 'leave') {
          strS = (-k * 0.55 + (rnd() - 0.5) * 0.08) * span;
          strN = ((k % 2 ? 1 : -1) * 0.55 + (rnd() - 0.5) * 0.08) * span;
        } else if (rise) {
          strS = fs * along * span;
          strN = fn * RISE_SPREAD * span;
        }
        const offS = plan.type === 'leave' ? strS : fs * span;
        const offN = plan.type === 'leave' ? strN : fn * span;
        f.tail = Math.max(f.tail, -Math.min(offS, strS));
        // Seen from below, the higher arm of a formation is a little nearer.
        const sizeMul = (1 - fn * 0.05) * lerp(0.96, 1.04, rnd());
        const startFlap = first || rise || rnd() < 0.7;
        const b = {
          slot: slots[k],
          offS,
          offN,
          strS,
          strN,
          span: span * sizeMul,
          amp: lerp(0.86, 1, depth),
          freq: lerp(4.0, 2.3, depth) * lerp(0.92, 1.08, rnd()),
          boost: rise ? 1.3 : 1,
          mode: startFlap ? FLAP : GLIDE,
          modeT: startFlap ? 1000 : rnd() * 500,
          theta: startFlap ? (first ? 0.3 + k * 0.7 + rnd() * 0.5 : rnd() * TAU) : GLIDE_THETA,
          beats: rise ? 6 + Math.floor(rnd() * 3) : (first ? 4 + Math.floor(rnd() * 3) : 2 + Math.floor(rnd() * 5)),
          glideDur: lerp(600, 2000, rnd()),
          e: startFlap ? 1 : 0,
          e0: startFlap ? 1 : 0,
          settleFrom: 0,
          settleDur: 1,
          alt: 0,
          vAlt: 0,
          altMax: fit.calm * ALT_MAX * span,
          sOff: 0,
          sep: 0,
          sepT: 0,
          sepS: 0,
          sepST: 0,
          rx: 0,
          ry: 0,
          s: f.dist + strS,
          dAx: lerp(0.05, 0.1, rnd()) * span,
          dAy: fit.calm * lerp(0.025, DRIFT_Y, rnd()) * span,
          dFx: lerp(0.05, 0.12, rnd()),
          dFy: lerp(0.06, 0.14, rnd()),
          dPx: rnd() * TAU,
          dPy: rnd() * TAU,
          yaw: (yaw + (rnd() - 0.5) * 8) * DEG,
          pitch: (pitch + (rnd() - 0.5) * 4) * DEG,
          rollPh: rnd() * TAU,
          rollF: lerp(0.1, 0.24, rnd()),
          opacity: r2(lerp(0.5, 1, depth)),
          // How far it is faded behind the text (phones; -1 = not yet known).
          veil: -1,
          rnd: seededRandom(seed + (k + 1) * 104729)
        };
        while (b.theta >= TAU) b.theta -= TAU;
        sim.used[b.slot] = b;
        f.birds.push(b);
      }
      return f;
    },

    /* ------------------------------------------------------------------ */
    /* Rendering                                                           */
    /* ------------------------------------------------------------------ */

    draw() {
      const sim = this.sim;
      const m = this.m;
      const moon = this.moonNow();
      const shown = this.shown;
      const shaded = this.shaded;
      for (let i = 0; i < SLOTS; i++) {
        shown[i] = false;
        shaded[i] = false;
      }
      let near = false;

      for (let i = 0; i < sim.flocks.length; i++) {
        const f = sim.flocks[i];
        const t = f.age / 1000;
        const v = f.speed * 1000;
        for (let j = 0; j < f.birds.length; j++) {
          const b = f.birds[j];
          const p = this.place(b, f, true);
          if (p.x < -p.span * 1.4 || p.x > m.W + p.span * 1.4 || p.y > m.H + p.span || p.y < -p.span) continue;
          const x = p.x;
          const s = p.s;
          const tx = p.tx;
          const ty = p.ty;
          const half = p.span / 2;
          let op = f.sc1 < 1 ? r2(b.opacity * lerp(f.fade, 1, (p.sc - f.sc1) / (1 - f.sc1))) : b.opacity;
          if (b.veil > 0.001) op = r2(op * (1 - (1 - VEIL) * b.veil));

          // Nose follows the path (and the bird's own climb or sink); the
          // path's curvature and a slow wobble bank the wings.
          const noseUp = clamp(0.85 * Math.atan2(-(ty * v + b.vAlt), Math.abs(tx) * v + 0.001), -0.32, 0.55);
          const pm = this.pathAt(f, s - 24, this.qa);
          const p0 = this.pathAt(f, s, this.qb);
          const pp = this.pathAt(f, s + 24, this.qc);
          const curve = tx * (pp.y - 2 * p0.y + pm.y) / 576 - ty * (pp.x - 2 * p0.x + pm.x) / 576;
          const roll = clamp(curve * f.dir * p.span * 8, -0.15, 0.15) + 0.06 * Math.sin(TAU * b.rollF * t + b.rollPh);
          // The body rises as the wings beat down, and dips as they come up.
          const bob = 0.075 * half * Math.cos(strokeAngle(b.theta)) * b.e;

          const y = p.y + bob;
          // Seen a little more head-on while it approaches and more from the
          // side as it flies off: a hint of the bird passing by in depth.
          const yaw = Math.min(b.yaw + (0.5 - clamp(s / f.L, 0, 1)) * 7 * DEG, 88 * DEG);
          // Distance from the moon's limb to the bird's reach (<0: touching).
          const gap = moon ? this.distToMoon(moon, x, y) - moon.r - p.span * 0.6 : 1e9;
          this.drawBird(this.nodes[b.slot], b, x, y, f.dir, noseUp, roll, half, yaw, gap < 0, op);
          shown[b.slot] = true;
          if (gap < 0) shaded[b.slot] = true;
          if (gap < 160) near = true;
        }
      }

      for (let i = 0; i < SLOTS; i++) {
        if (!shown[i]) this.hideNode(this.nodes[i]);
        if (!shaded[i]) this.hideNode(this.shades[i]);
      }
      this.moonNear = near;
    },

    distToMoon(moon, x, y) {
      const dx = x - moon.x;
      const dy = y - moon.y;
      return Math.sqrt(dx * dx + dy * dy);
    },

    // Writes one bird's outlines into its node (and into its moon silhouette
    // when `shaded`, so both always match exactly).
    drawBird(node, b, x, y, dir, noseUp, roll, half, yaw, shaded, op) {
      if (!node) return;
      flapPose(b.theta, b.e, b.amp, this.pose);
      camera(yaw, b.pitch, this.wingCam);
      camera(yaw - VIEW.bodyTurn * DEG, b.pitch, this.bodyCam);
      const d = birdPaths(this.pose, this.wingCam, this.bodyCam, roll, half);
      const transform = 'translate(' + r2(x) + ' ' + r2(y) + ') scale(' + dir + ' 1) rotate(' + r2(-noseUp / DEG) + ')';
      this.writeNode(node, d, transform, op);
      if (shaded) this.writeNode(this.shades[b.slot], d, transform, op);
    },

    writeNode(node, d, transform, op) {
      if (!node) return;
      node.far.setAttribute('d', d.far);
      node.body.setAttribute('d', d.body);
      node.near.setAttribute('d', d.near);
      node.g.setAttribute('transform', transform);
      if (node.op !== op) {
        node.op = op;
        node.g.setAttribute('opacity', op);
      }
      if (!node.shown) {
        node.shown = true;
        node.g.removeAttribute('display');
      }
    },

    hideNode(node) {
      if (!node || !node.shown) return;
      node.shown = false;
      node.g.setAttribute('display', 'none');
    },

    // Every bird on screen right now (for checks): centre, wingspan and the
    // vertical reach of its wings.
    snapshot() {
      const out = [];
      const sim = this.sim;
      for (let i = 0; i < sim.flocks.length; i++) {
        const f = sim.flocks[i];
        for (let j = 0; j < f.birds.length; j++) {
          const b = f.birds[j];
          const p = this.place(b, f, true);
          out.push({ flock: i, id: f.id + ':' + j, x: r2(p.x), y: r2(p.y), span: r2(p.span), up: r2(p.span * REACH.up), down: r2(p.span * REACH.down), veil: r2(Math.max(0, b.veil)) });
        }
      }
      return out;
    },

    // Reduced motion: a small group frozen mid-glide (one bird mid-stroke)
    // in a clear patch of sky, preferably the upper left, instead of an
    // empty sky.
    renderStill() {
      this.loop.stop();
      this.sim = this.createSim();
      const m = this.m;
      for (let i = 0; i < SLOTS; i++) {
        this.hideNode(this.nodes[i]);
        this.hideNode(this.shades[i]);
      }
      const fit = this.fitFormation(SEED + 3, m.phone ? 2 : 3, m.phone ? 'pair' : 'v', 0.78, true);
      const span = fit.span;
      const birds = [
        { dx: 0, dy: 0, size: 1, yaw: 82, theta: GLIDE_THETA, e: 0, nose: 0.04 },
        { dx: -1.25, dy: -0.55, size: 1.03, yaw: 79, theta: 4.6, e: 0.35, nose: 0.07 },
        { dx: -1.6, dy: 0.5, size: 0.95, yaw: 85, theta: GLIDE_THETA, e: 0.1, nose: 0.02 }
      ].slice(0, m.phone ? 2 : 3);
      const above = span * (0.55 + REACH.up);
      const below = span * (0.5 + REACH.down);
      const fits = (x, y, withSoft) => birds.every((s) => !this.blocked(x + s.dx * span, y + s.dy * span, span * 0.55, span * 0.4, withSoft));
      const tries = [0.2, 0.14, 0.26, 0.8, 0.86, 0.5];
      let lead = null;
      // Clear sky first; on a phone, failing that, behind the text (faded
      // there, like a bird flying by).
      for (let pass = 0; pass < 2 && !lead; pass++) {
        for (let i = 0; i < tries.length && !lead; i++) {
          const x = m.W * tries[i];
          let y = Math.min(lerp(m.bandTop, this.bandBottom(), 0.22), this.ceilAt(x - span * 2) - below, this.ceilAt(x) - below, this.ceilAt(x + span * 0.6) - below);
          y = Math.max(y, m.top + above);
          if (fits(x, y, pass === 0)) lead = { x, y };
        }
      }
      if (!lead) lead = { x: m.W * 0.2, y: m.top + above };
      birds.forEach((s, k) => {
        const x = lead.x + s.dx * span;
        const y = lead.y + s.dy * span;
        const b = {
          slot: SLOTS - 1 - k,
          span: span * s.size,
          theta: s.theta,
          e: s.e,
          amp: 1,
          yaw: s.yaw * DEG,
          pitch: 28 * DEG,
          opacity: 0.92
        };
        const op = r2(b.opacity * (1 - (1 - VEIL) * this.veilAt(x, y, b.span)));
        this.drawBird(this.nodes[b.slot], b, x, y, 1, s.nose, 0.03 * (k - 1), b.span / 2, b.yaw, false, op);
      });
    }
  }
}
</script>

<style scoped lang="scss">
// The sky's own crossfade curve and timing, so the birds change colour
// together with it when the theme switches.
$theme-ease: cubic-bezier(0.45, 0.05, 0.55, 0.95);
$theme-fade: 1.4s $theme-ease 0.12s;

.hero-birds {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  // Moonlit, pale silhouettes by night (kept soft so they never compete
  // with the white title)...
  --bird-ink: #cfdcef;
  --bird-ink-far: #9badc6;
  --bird-alpha: 0.62;
}

// ...and dark ones against the morning sky.
[data-theme="light"] .hero-birds {
  --bird-ink: #25392c;
  --bird-ink-far: #16261b;
  --bird-alpha: 0.85;
}

.hero-birds__parallax {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translate3d(calc(var(--hero-px, 0) * 10px), calc(var(--hero-py, 0) * 5px), 0);
}

.hero-birds__svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  // Birds entering or leaving stay whole; .hero-birds clips at the hero.
  overflow: visible;
}

.hero-birds__flock {
  opacity: var(--bird-alpha);
  transition: opacity $theme-fade;
}

.hero-bird__body,
.hero-bird__near {
  fill: var(--bird-ink);
  // A hairline of the same ink keeps thin, edge-on wings and the smallest,
  // farthest birds from breaking up into sub-pixel slivers.
  stroke: var(--bird-ink);
  stroke-width: 0.5px;
  stroke-linejoin: round;
  transition: fill $theme-fade, stroke $theme-fade;
}

// The far wing sits a touch deeper in shadow.
.hero-bird__far {
  fill: var(--bird-ink-far);
  stroke: var(--bird-ink-far);
  stroke-width: 0.5px;
  stroke-linejoin: round;
  transition: fill $theme-fade, stroke $theme-fade;
}

// Against the bright moon a bird is a true silhouette.
.hero-bird-shade path {
  fill: #0d1724;
  stroke: #0d1724;
  stroke-width: 0.5px;
  stroke-linejoin: round;
}
</style>
